/**
 * Chain Preset Selector Component
 * UI for browsing and loading plugin chain presets
 */

import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import {
  Search,
  X,
  Bookmark,
  Save,
  Trash2,
  ChevronDown,
  Sparkles,
  Music,
  Mic,
  Gauge,
  User,
  Grid3X3,
  Zap,
  Radio,
  Coffee,
  Leaf,
  Film,
  Sun,
  Disc,
  Minus,
  Check,
  FolderOpen,
  Share2,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  FACTORY_CHAIN_PRESETS,
  PRESET_CATEGORIES,
  getUserPresets,
  saveUserPreset,
  deleteUserPreset,
  getAllPresets,
  serializeChainToPreset,
} from './ChainPresets';

// Icon mapping for presets
const PRESET_ICONS = {
  sparkles: Sparkles,
  zap: Zap,
  music: Music,
  radio: Radio,
  coffee: Coffee,
  guitar: Music,
  leaf: Leaf,
  film: Film,
  sun: Sun,
  disc: Disc,
  minus: Minus,
  mic: Mic,
  bookmark: Bookmark,
  gauge: Gauge,
  user: User,
  grid: Grid3X3,
};

// Category colors
const CATEGORY_COLORS = {
  mastering: 'from-amber-500 to-orange-500',
  genre: 'from-purple-500 to-indigo-500',
  voice: 'from-cyan-500 to-blue-500',
  creative: 'from-pink-500 to-rose-500',
  user: 'from-emerald-500 to-teal-500',
};

/**
 * Preset Card Component
 */
const PresetCard = ({ preset, onLoad, onDelete, isUserPreset }) => {
  const IconComponent = PRESET_ICONS[preset.icon] || Bookmark;
  const colorClass = CATEGORY_COLORS[preset.category] || 'from-zinc-500 to-zinc-400';
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="group relative p-3 bg-zinc-800/50 hover:bg-zinc-700/50 rounded-lg border border-zinc-700/50 hover:border-zinc-600 transition-all cursor-pointer"
      onClick={() => onLoad(preset)}
      data-testid={`chain-preset-${preset.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={cn(
          "w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br flex-shrink-0",
          colorClass
        )}>
          <IconComponent className="w-5 h-5 text-white" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-medium text-white truncate">{preset.name}</h4>
            {isUserPreset && (
              <span className="text-[9px] px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 rounded">
                Custom
              </span>
            )}
          </div>
          <p className="text-xs text-zinc-400 line-clamp-2 mt-0.5">{preset.description}</p>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-[10px] text-zinc-500">
              {preset.plugins?.length || 0} plugins
            </span>
            <span className="text-[10px] text-zinc-600">•</span>
            <span className="text-[10px] text-zinc-500 capitalize">{preset.category}</span>
          </div>
        </div>
      </div>
      
      {/* Delete button for user presets */}
      {isUserPreset && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(preset.id);
          }}
          className="absolute top-2 right-2 w-6 h-6 rounded bg-red-500/20 hover:bg-red-500/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          title="Delete preset"
        >
          <Trash2 className="w-3 h-3 text-red-400" />
        </button>
      )}
      
      {/* Load indicator */}
      <div className="absolute inset-0 rounded-lg border-2 border-transparent group-hover:border-cyan-500/30 transition-colors pointer-events-none" />
    </motion.div>
  );
};

/**
 * Save Preset Modal
 */
const SavePresetModal = ({ isOpen, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  
  const handleSave = () => {
    if (!name.trim()) {
      toast.error('Please enter a preset name');
      return;
    }
    onSave(name.trim(), description.trim());
    setName('');
    setDescription('');
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 10 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 10 }}
        className="w-full max-w-md bg-zinc-900 rounded-xl border border-zinc-700 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="save-preset-modal"
      >
        <div className="p-4 border-b border-zinc-800">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Save className="w-5 h-5 text-cyan-400" />
            Save Chain Preset
          </h3>
        </div>
        
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm text-zinc-300 mb-1.5">Preset Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Custom Chain"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
              data-testid="preset-name-input"
              autoFocus
            />
          </div>
          
          <div>
            <label className="block text-sm text-zinc-300 mb-1.5">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your preset..."
              rows={2}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 resize-none"
              data-testid="preset-description-input"
            />
          </div>
        </div>
        
        <div className="p-4 border-t border-zinc-800 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} className="bg-cyan-500 hover:bg-cyan-600">
            <Save className="w-4 h-4 mr-1.5" />
            Save Preset
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

/**
 * Main Chain Preset Selector Component
 */
export const ChainPresetSelector = ({
  isOpen,
  onClose,
  onLoadPreset,
  currentPlugins = [],
  onSavePreset,
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showSaveModal, setShowSaveModal] = useState(false);
  
  // Handle sharing to marketplace
  const handleShareToMarketplace = () => {
    if (currentPlugins.length === 0) {
      toast.error('Add some plugins before sharing');
      return;
    }
    
    // Store the current plugin chain for the upload page
    const presetData = {
      pluginChain: currentPlugins.map(p => ({
        id: p.id,
        type: p.type || p.name?.toLowerCase().replace(/\s+/g, '-'),
        name: p.name,
        enabled: p.enabled !== false,
        params: p.params || p.getState?.()?.parameters || {}
      })),
      presetType: 'master',
      name: 'Master Chain',
    };
    
    localStorage.setItem('presetToShare', JSON.stringify(presetData));
    onClose?.();
    navigate('/marketplace/upload');
    toast.success('Opening marketplace upload...');
  };
  
  // Get all presets including user presets
  const allPresets = useMemo(() => getAllPresets(), []);
  const userPresets = useMemo(() => getUserPresets(), []);
  
  // Filter presets
  const filteredPresets = useMemo(() => {
    return Object.values(allPresets).filter(preset => {
      // Category filter
      if (selectedCategory !== 'all' && preset.category !== selectedCategory) {
        return false;
      }
      
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          preset.name.toLowerCase().includes(query) ||
          preset.description?.toLowerCase().includes(query)
        );
      }
      
      return true;
    });
  }, [allPresets, selectedCategory, searchQuery]);
  
  const handleLoadPreset = (preset) => {
    onLoadPreset?.(preset);
    onClose?.();
    toast.success(`Loaded "${preset.name}" preset`);
  };
  
  const handleDeletePreset = (presetId) => {
    if (deleteUserPreset(presetId)) {
      toast.success('Preset deleted');
    }
  };
  
  const handleSavePreset = (name, description) => {
    if (currentPlugins.length === 0) {
      toast.error('Add some plugins before saving');
      return;
    }
    
    const preset = serializeChainToPreset(currentPlugins, name, description);
    if (saveUserPreset(preset)) {
      toast.success(`Saved "${name}" preset`);
      onSavePreset?.(preset);
    } else {
      toast.error('Failed to save preset');
    }
  };
  
  // Count presets by category
  const categoryCounts = useMemo(() => {
    const counts = { all: 0 };
    Object.values(allPresets).forEach(preset => {
      counts.all++;
      counts[preset.category] = (counts[preset.category] || 0) + 1;
    });
    return counts;
  }, [allPresets]);
  
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-4 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-[700px] sm:max-h-[85vh] bg-zinc-900 rounded-xl border border-zinc-700 shadow-2xl z-50 flex flex-col overflow-hidden"
            data-testid="chain-preset-selector"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-zinc-800 bg-gradient-to-r from-cyan-500/10 to-purple-500/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center">
                  <FolderOpen className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Chain Presets</h2>
                  <p className="text-xs text-zinc-400">Load or save plugin chain configurations</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Share to Marketplace Button */}
                <Button
                  onClick={handleShareToMarketplace}
                  size="sm"
                  variant="outline"
                  className="text-xs border-violet-500/50 text-violet-400 hover:bg-violet-500/20"
                  disabled={currentPlugins.length === 0}
                  data-testid="share-master-to-marketplace-btn"
                >
                  <Share2 className="w-3.5 h-3.5 mr-1.5" />
                  Share
                </Button>
                {/* Save Current Chain Button */}
                <Button
                  onClick={() => setShowSaveModal(true)}
                  size="sm"
                  variant="outline"
                  className="text-xs"
                  disabled={currentPlugins.length === 0}
                  data-testid="save-chain-btn"
                >
                  <Save className="w-3.5 h-3.5 mr-1.5" />
                  Save Current
                </Button>
                <button
                  onClick={onClose}
                  className="p-1.5 text-zinc-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Search & Categories */}
            <div className="p-4 border-b border-zinc-800 space-y-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  type="text"
                  placeholder="Search presets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                  data-testid="preset-search-input"
                />
              </div>
              
              {/* Category Tabs */}
              <div className="flex flex-wrap gap-1">
                {Object.entries(PRESET_CATEGORIES).map(([key, cat]) => {
                  const IconComponent = PRESET_ICONS[cat.icon] || Grid3X3;
                  const count = categoryCounts[key] || 0;
                  return (
                    <button
                      key={key}
                      onClick={() => setSelectedCategory(key)}
                      className={cn(
                        "px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5",
                        selectedCategory === key
                          ? "bg-white text-black"
                          : "bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700"
                      )}
                    >
                      <IconComponent className="w-3.5 h-3.5" />
                      {cat.name}
                      <span className={cn(
                        "text-[10px] px-1 rounded",
                        selectedCategory === key ? "bg-black/20" : "bg-zinc-700"
                      )}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Preset Grid */}
            <div className="flex-1 overflow-y-auto p-4">
              {filteredPresets.length === 0 ? (
                <div className="text-center py-12 text-zinc-500">
                  <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No presets found</p>
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="text-cyan-400 text-sm mt-2 hover:underline"
                    >
                      Clear search
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {filteredPresets.map((preset) => (
                    <PresetCard
                      key={preset.id}
                      preset={preset}
                      onLoad={handleLoadPreset}
                      onDelete={handleDeletePreset}
                      isUserPreset={!!userPresets[preset.id]}
                    />
                  ))}
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-zinc-800 bg-zinc-900/80">
              <div className="flex items-center justify-between text-xs text-zinc-500">
                <span>
                  {filteredPresets.length} preset{filteredPresets.length !== 1 ? 's' : ''} •{' '}
                  {Object.keys(userPresets).length} custom
                </span>
                <span>Click to load • Custom presets saved locally</span>
              </div>
            </div>
          </motion.div>
          
          {/* Save Modal */}
          <SavePresetModal
            isOpen={showSaveModal}
            onClose={() => setShowSaveModal(false)}
            onSave={handleSavePreset}
          />
        </>
      )}
    </AnimatePresence>
  );
};

export default ChainPresetSelector;
